// Account Settings → TruXSocial Premium (1.27.70).
//
// HONESTY: the perks listed are exactly the ones core/data/premium.ts says Pro
// delivers today (features/premium/meta.ts PRO_ACTIVE_BENEFIT_TITLES), read
// through usePremiumSubscribed. The concept's "verified badge", "custom app
// themes" and "priority support" are deliberately absent: a checkmark is earned,
// never bought; themes are free for everyone; support is only planned.
import styled from 'styled-components';
import { Icon } from '@/icons';
import { usePremiumSubscribed } from '@/hooks';
import { isProActiveBenefit } from '@/features/premium/meta';
import { DashCard } from '../../dashboard/DashCard';
import { LinkButton } from '../../dashboard/dashStyles';

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  > svg {
    flex: none;
    color: ${({ theme }) => theme.color.supporter};
  }
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: ${({ theme }) => theme.lineHeight.md};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Sub = styled.p`
  margin: 2px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Blurb = styled.p`
  margin: 12px 0 10px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Perks = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Perk = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  > svg {
    flex: none;
    margin-top: 2px;
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Note = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Foot = styled.div`
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;
`;

/* Decorative watermark, the only ornament on the card. */
const Watermark = styled.span`
  position: absolute;
  right: 18px;
  top: 18px;
  opacity: 0.12;
  pointer-events: none;
  color: ${({ theme }) => theme.color.accentCyan};
`;

export function PremiumCard({ isPremium }: { isPremium: boolean }) {
  const { ultraBenefits } = usePremiumSubscribed();
  const perks = ultraBenefits.filter((b) => isProActiveBenefit(b.title));
  return (
    <DashCard
      labelledBy="acct-premium-title"
      span={2}
      spanMid={2}
      tone="accent"
      testId="premium-card"
    >
      <Watermark aria-hidden="true">
        <Icon name="crown" size={72} />
      </Watermark>
      <Head>
        <Icon name="crown" size={28} />
        <div>
          <Title id="acct-premium-title">TruXSocial Premium</Title>
          <Sub>{isPremium ? 'You are a Pro supporter' : 'Get more from TruXSocial'}</Sub>
        </div>
      </Head>
      <Blurb>
        {isPremium
          ? 'Thank you. Your subscription keeps TruXSocial free of ads and data sales.'
          : 'Back the platform directly and unlock supporter perks. No ads, no data sales.'}
      </Blurb>
      <Perks aria-label="What Pro includes">
        {perks.map((p) => (
          <Perk key={p.title} title={p.blurb}>
            <Icon name="check" size={14} />
            {p.title}
          </Perk>
        ))}
      </Perks>
      {perks.some((p) => p.title === 'Supporter Badge') && (
        <Note>The supporter badge shows once two-factor sign-in is on.</Note>
      )}
      <Foot>
        <LinkButton to="/premium" $variant={isPremium ? 'secondary' : 'primary'}>
          {isPremium ? 'Manage Premium' : 'Upgrade to Premium'}
          <Icon name="arrow-right" size={14} />
        </LinkButton>
      </Foot>
    </DashCard>
  );
}
